import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Eye, EyeOff, CheckCircle, Loader2, RefreshCw, Trash2, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface CrmConnection {
  id: number;
  provider: string;
  brandId: number | null;
  siteId: string | null;
  isActive: boolean;
  lastSyncAt: string | null;
  apiKey: string;
}

interface ConnectionStatus {
  lastSyncAt: string | null;
  customerCount: number;
  orderCount: number;
  isActive: boolean;
}

interface Brand {
  id: number;
  name: string;
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return "Never";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} minute${mins !== 1 ? "s" : ""} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
  return `${Math.floor(hours / 24)} day${Math.floor(hours / 24) !== 1 ? "s" : ""} ago`;
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  border: "1px solid #E5E2DC",
  borderRadius: 7,
  padding: "8px 12px",
  fontSize: 13,
  color: "#1A1A1A",
  background: "#fff",
  outline: "none",
  boxSizing: "border-box",
};

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: "#6B6B6B",
  marginBottom: 5,
  display: "block",
};

const hintStyle: React.CSSProperties = {
  fontSize: 11,
  color: "#B5B0A8",
  marginTop: 4,
};

const btnPrimary: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 16px",
  borderRadius: 7,
  fontSize: 13,
  fontWeight: 600,
  background: "#6366F1",
  color: "#fff",
  border: "none",
  cursor: "pointer",
  transition: "opacity 150ms",
};

const btnSecondary: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 14px",
  borderRadius: 7,
  fontSize: 13,
  fontWeight: 500,
  background: "transparent",
  color: "#4A4A4A",
  border: "1px solid #E5E2DC",
  cursor: "pointer",
  transition: "background 120ms",
};

const btnDanger: React.CSSProperties = {
  ...btnSecondary,
  color: "#DC2626",
  border: "1px solid #FCA5A5",
};

function ConnectForm() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [apiKey, setApiKey] = useState("");
  const [siteId, setSiteId] = useState("");
  const [brandId, setBrandId] = useState<string>("");
  const [showKey, setShowKey] = useState(false);
  const [testState, setTestState] = useState<"idle" | "testing" | "ok" | "error">("idle");
  const [testMsg, setTestMsg] = useState("");
  const [customerCount, setCustomerCount] = useState<number | null>(null);

  const { data: brands = [] } = useQuery<Brand[]>({
    queryKey: ["/api/brands"],
    queryFn: () => fetch("/api/brands").then(r => r.json()),
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/crm/connections", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          apiKey,
          siteId: siteId || undefined,
          brandId: brandId ? Number(brandId) : undefined,
          provider: "responsecrm",
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Failed to save" }));
        throw new Error(err.message || "Failed to save");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/connections"] });
      toast({ title: "CRM Connected", description: "Your CRM is connected. Syncing data now…" });
      setApiKey("");
      setSiteId("");
      setBrandId("");
      setTestState("idle");
    },
    onError: (err: Error) => {
      toast({ title: "Connection failed", description: err.message, variant: "destructive" });
    },
  });

  const handleTest = async () => {
    if (!apiKey.trim()) return;
    setTestState("testing");
    setTestMsg("");
    try {
      const res = await fetch("/api/crm/connections", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ apiKey, siteId: siteId || undefined, testOnly: true }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setTestState("error");
        setTestMsg(data.message || "Connection failed");
      } else {
        setTestState("ok");
        setCustomerCount(data.customerCount ?? null);
        setTestMsg(
          customerCount !== null
            ? `Connected — ${data.customerCount?.toLocaleString() ?? "?"} customers found`
            : "Connected successfully"
        );
      }
    } catch (err: any) {
      setTestState("error");
      setTestMsg(err?.message || "Network error");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#1A1A1A" }}>Admoji CRM (ResponseCRM)</div>

      <div>
        <label style={labelStyle}>API Key</label>
        <div style={{ position: "relative" }}>
          <input
            data-testid="crm-api-key-input"
            type={showKey ? "text" : "password"}
            value={apiKey}
            onChange={e => { setApiKey(e.target.value); setTestState("idle"); }}
            placeholder="Paste your API key…"
            style={{ ...inputStyle, paddingRight: 36 }}
          />
          <button
            type="button"
            onClick={() => setShowKey(v => !v)}
            style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#8C8578" }}
          >
            {showKey ? <EyeOff style={{ width: 14, height: 14 }} /> : <Eye style={{ width: 14, height: 14 }} />}
          </button>
        </div>
        <p style={hintStyle}>Paste from CRM → Administration → Accounts</p>
      </div>

      <div>
        <label style={labelStyle}>Site ID <span style={{ fontWeight: 400, color: "#B5B0A8" }}>(optional)</span></label>
        <input
          data-testid="crm-site-id-input"
          type="text"
          value={siteId}
          onChange={e => setSiteId(e.target.value)}
          placeholder="e.g. 12345"
          style={inputStyle}
        />
      </div>

      <div>
        <label style={labelStyle}>Brand <span style={{ fontWeight: 400, color: "#B5B0A8" }}>(optional)</span></label>
        <select
          data-testid="crm-brand-select"
          value={brandId}
          onChange={e => setBrandId(e.target.value)}
          style={{ ...inputStyle, appearance: "none" }}
        >
          <option value="">— Select brand —</option>
          {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>

      {testState === "ok" && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#16A34A", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 7, padding: "8px 12px" }}>
          <CheckCircle style={{ width: 14, height: 14, flexShrink: 0 }} />
          {customerCount !== null
            ? `Connected — ${customerCount.toLocaleString()} customers found`
            : "Connected successfully"}
        </div>
      )}
      {testState === "error" && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#DC2626", background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 7, padding: "8px 12px" }}>
          <AlertCircle style={{ width: 14, height: 14, flexShrink: 0 }} />
          {testMsg}
        </div>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <button
          data-testid="crm-test-btn"
          onClick={handleTest}
          disabled={!apiKey.trim() || testState === "testing"}
          style={{ ...btnSecondary, opacity: !apiKey.trim() ? 0.5 : 1 }}
        >
          {testState === "testing" && <Loader2 style={{ width: 13, height: 13, animation: "spin 1s linear infinite" }} />}
          Test Connection
        </button>
        <button
          data-testid="crm-save-btn"
          onClick={() => saveMutation.mutate()}
          disabled={!apiKey.trim() || testState !== "ok" || saveMutation.isPending}
          style={{ ...btnPrimary, opacity: (testState !== "ok" || saveMutation.isPending) ? 0.5 : 1 }}
        >
          {saveMutation.isPending && <Loader2 style={{ width: 13, height: 13, animation: "spin 1s linear infinite" }} />}
          Save & Connect
        </button>
      </div>
    </div>
  );
}

function ConnectedView({ connection }: { connection: CrmConnection }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [changingKey, setChangingKey] = useState(false);

  const { data: status } = useQuery<ConnectionStatus>({
    queryKey: ["/api/crm/connections", connection.id, "status"],
    queryFn: () => fetch(`/api/crm/connections/${connection.id}/status`).then(r => r.json()),
    refetchInterval: 60000,
  });

  const syncMutation = useMutation({
    mutationFn: () => fetch(`/api/crm/connections/${connection.id}/sync`, { method: "POST" }).then(r => r.json()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/connections"] });
      toast({ title: "Synced", description: "CRM data refreshed successfully." });
    },
    onError: () => {
      toast({ title: "Sync failed", description: "Could not sync CRM data.", variant: "destructive" });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => fetch(`/api/crm/connections/${connection.id}`, { method: "DELETE" }).then(r => r.json()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/connections"] });
      toast({ title: "Disconnected", description: "CRM connection removed." });
    },
  });

  if (changingKey) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#1A1A1A" }}>Admoji CRM (ResponseCRM)</span>
          <button onClick={() => setChangingKey(false)} style={{ ...btnSecondary, fontSize: 12 }}>Cancel</button>
        </div>
        <ConnectForm />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#1A1A1A" }}>Admoji CRM (ResponseCRM)</span>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: "#16A34A", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 20, padding: "3px 10px", display: "flex", alignItems: "center", gap: 4 }}>
          <span>●</span> Connected
        </span>
      </div>

      <div style={{ background: "#FAFAF8", border: "1px solid #E8E5E0", borderRadius: 10, overflow: "hidden" }}>
        {[
          { label: "API Key", value: connection.apiKey },
          { label: "Site ID", value: connection.siteId || "—" },
          { label: "Last Synced", value: timeAgo(status?.lastSyncAt ?? connection.lastSyncAt) },
          {
            label: "Records",
            value: status ? `${status.customerCount.toLocaleString()} customers · ${status.orderCount.toLocaleString()} orders` : "Loading…",
          },
        ].map((row, i) => (
          <div key={row.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 14px", borderBottom: i < 3 ? "1px solid #F0EDE9" : undefined }}>
            <span style={{ fontSize: 12.5, color: "#8C8578" }}>{row.label}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 12.5, fontWeight: 500, color: "#1A1A1A" }}>{row.value}</span>
              {row.label === "API Key" && (
                <button onClick={() => setChangingKey(true)} style={{ fontSize: 11.5, color: "#6366F1", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>
                  Change
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          data-testid="crm-sync-now-btn"
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
          style={{ ...btnSecondary }}
        >
          {syncMutation.isPending
            ? <Loader2 style={{ width: 13, height: 13, animation: "spin 1s linear infinite" }} />
            : <RefreshCw style={{ width: 13, height: 13 }} />}
          {syncMutation.isPending ? "Syncing…" : "Sync Now"}
        </button>

        {confirmDisconnect ? (
          <>
            <span style={{ fontSize: 12.5, color: "#8C8578", alignSelf: "center" }}>Disconnect?</span>
            <button
              data-testid="crm-disconnect-confirm-btn"
              onClick={() => disconnectMutation.mutate()}
              disabled={disconnectMutation.isPending}
              style={btnDanger}
            >
              {disconnectMutation.isPending ? <Loader2 style={{ width: 13, height: 13, animation: "spin 1s linear infinite" }} /> : <Trash2 style={{ width: 13, height: 13 }} />}
              Yes, disconnect
            </button>
            <button onClick={() => setConfirmDisconnect(false)} style={btnSecondary}>Cancel</button>
          </>
        ) : (
          <button
            data-testid="crm-disconnect-btn"
            onClick={() => setConfirmDisconnect(true)}
            style={btnDanger}
          >
            <Trash2 style={{ width: 13, height: 13 }} />
            Disconnect
          </button>
        )}
      </div>
    </div>
  );
}

function ComingSoonProviders() {
  const providers = ["Shopify", "WooCommerce", "BigCommerce"];
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <div style={{ flex: 1, height: 1, background: "#E8E5E0" }} />
        <span style={{ fontSize: 11, fontWeight: 600, color: "#C4BFB9", textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>Coming Soon</span>
        <div style={{ flex: 1, height: 1, background: "#E8E5E0" }} />
      </div>
      {providers.map(p => (
        <div key={p} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 0", borderBottom: "1px solid #F5F3F0" }}>
          <span style={{ fontSize: 13, color: "#B5B0A8" }}>{p}</span>
          <span style={{ fontSize: 11.5, color: "#C4BFB9", fontStyle: "italic" }}>Coming soon</span>
        </div>
      ))}
    </div>
  );
}

export function CrmConnectionSettings() {
  const { data: connections = [], isLoading } = useQuery<CrmConnection[]>({
    queryKey: ["/api/crm/connections"],
    queryFn: () => fetch("/api/crm/connections").then(r => r.json()),
  });

  const activeConnection = connections.find(c => c.isActive);

  if (isLoading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 80, color: "#8C8578", fontSize: 13 }}>
        Loading…
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 440, display: "flex", flexDirection: "column", gap: 0 }}>
      {activeConnection ? (
        <ConnectedView connection={activeConnection} />
      ) : (
        <ConnectForm />
      )}
      <ComingSoonProviders />
    </div>
  );
}
